import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  updateDoc, 
  doc, 
  Timestamp,
  deleteDoc,
  limit,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  recipientId: string | 'all'; // 'all' for broadcast notifications
  senderId: string;
  senderName: string;
  createdAt: Timestamp;
  readAt?: Timestamp;
  isRead: boolean;
  expiresAt?: Timestamp;
  deletedBy?: string[];
  deletedAt?: Timestamp;
}

export interface CreateNotificationData {
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  recipientId: string | 'all';
  senderId: string;
  senderName: string;
  expiresAt?: Date;
}

export class NotificationService {
  // Create a new notification
  static async createNotification(data: CreateNotificationData): Promise<string> {
    try {
      console.log('NotificationService: Creating notification with data:', data);
      
      const notificationData = {
        title: data.title,
        message: data.message,
        type: data.type,
        recipientId: data.recipientId,
        senderId: data.senderId,
        senderName: data.senderName,
        createdAt: Timestamp.now(),
        isRead: false,
        expiresAt: data.expiresAt ? Timestamp.fromDate(data.expiresAt) : null
      };

      console.log('NotificationService: Prepared notification data:', notificationData);
      
      const docRef = await addDoc(collection(db, 'notifications'), notificationData);
      console.log('NotificationService: Notification created with ID:', docRef.id);
      
      return docRef.id;
    } catch (error) {
      console.error('NotificationService: Error creating notification:', error);
      throw error;
    }
  }

  // Get notifications for a specific user with pagination
  static async getUserNotifications(userId: string, limitCount: number = 10, offset: number = 0): Promise<{ notifications: Notification[], totalCount: number }> {
    try {
      const notificationsRef = collection(db, 'notifications');
      
      // Get user-specific and broadcast notifications separately to avoid composite index requirement
      const [userNotifications, broadcastNotifications] = await Promise.all([
        getDocs(query(
          notificationsRef,
          where('recipientId', '==', userId)
        )),
        getDocs(query(
          notificationsRef,
          where('recipientId', '==', 'all')
        ))
      ]);

      // Combine results
      const allDocs = [...userNotifications.docs, ...broadcastNotifications.docs];
      
      // Filter out soft-deleted notifications for this user
      const filteredDocs = allDocs.filter((doc: any) => {
        const data = doc.data();
        const deletedBy = data.deletedBy || [];
        return !deletedBy.includes(userId);
      });
      
      // Sort by createdAt
      const sortedDocs = filteredDocs.sort((a, b) => {
        const aTime = a.data().createdAt?.toMillis() || 0;
        const bTime = b.data().createdAt?.toMillis() || 0;
        return bTime - aTime;
      });

      // Apply pagination
      const totalCount = sortedDocs.length;
      const paginatedDocs = sortedDocs.slice(offset, offset + limitCount);

      const notifications: Notification[] = [];

      paginatedDocs.forEach((doc: any) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          ...data
        } as Notification);
      });

      return { notifications, totalCount };
    } catch (error) {
      console.error('Error fetching user notifications:', error);
      throw error;
    }
  }

  // Get unread notification count for a user
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const notificationsRef = collection(db, 'notifications');
      
      // Get user-specific and broadcast notifications separately to avoid composite index requirement
      const [userNotifications, broadcastNotifications] = await Promise.all([
        getDocs(query(
          notificationsRef,
          where('recipientId', '==', userId),
          where('isRead', '==', false)
        )),
        getDocs(query(
          notificationsRef,
          where('recipientId', '==', 'all'),
          where('isRead', '==', false)
        ))
      ]);

      // Combine and filter out soft-deleted notifications
      const allDocs = [...userNotifications.docs, ...broadcastNotifications.docs];
      const filteredDocs = allDocs.filter((doc: any) => {
        const data = doc.data();
        const deletedBy = data.deletedBy || [];
        return !deletedBy.includes(userId);
      });

      return filteredDocs.length;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      throw error;
    }
  }

  // Mark notification as read
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        isRead: true,
        readAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  // Mark all notifications as read for a user
  static async markAllAsRead(userId: string): Promise<void> {
    try {
      const notificationsRef = collection(db, 'notifications');
      
      // Get user-specific and broadcast notifications separately
      const [userNotifications, broadcastNotifications] = await Promise.all([
        getDocs(query(
          notificationsRef,
          where('recipientId', '==', userId),
          where('isRead', '==', false)
        )),
        getDocs(query(
          notificationsRef,
          where('recipientId', '==', 'all'),
          where('isRead', '==', false)
        ))
      ]);

      // Combine and filter out soft-deleted notifications
      const allDocs = [...userNotifications.docs, ...broadcastNotifications.docs];
      const filteredDocs = allDocs.filter((doc: any) => {
        const data = doc.data();
        const deletedBy = data.deletedBy || [];
        return !deletedBy.includes(userId);
      });
      
      const updatePromises = filteredDocs.map((doc: any) => 
        updateDoc(doc.ref, {
          isRead: true,
          readAt: Timestamp.now()
        })
      );

      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  // Soft delete notification (mark as deleted for user)
  static async softDeleteNotification(notificationId: string, userId: string): Promise<void> {
    try {
      console.log('Service: Soft deleting notification', notificationId, 'for user', userId);
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        deletedBy: arrayUnion(userId),
        deletedAt: Timestamp.now()
      });
      console.log('Service: Successfully soft deleted notification', notificationId);
    } catch (error) {
      console.error('Error soft deleting notification:', error);
      throw error;
    }
  }

  // Delete notification (permanent delete from database)
  static async deleteNotification(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await deleteDoc(notificationRef);
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  }

  // Get all notifications (admin only)
  static async getAllNotifications(limitCount: number = 100): Promise<Notification[]> {
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const notifications: Notification[] = [];

      querySnapshot.forEach((doc: any) => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          ...data
        } as Notification);
      });

      return notifications;
    } catch (error) {
      console.error('Error fetching all notifications:', error);
      throw error;
    }
  }

  // Send notification to all users
  static async sendToAllUsers(
    title: string, 
    message: string, 
    type: 'info' | 'success' | 'warning' | 'error',
    senderId: string,
    senderName: string,
    expiresAt?: Date
  ): Promise<string> {
    try {
      console.log('NotificationService: Sending notification to all users');
      
      const notificationData: CreateNotificationData = {
        title,
        message,
        type,
        recipientId: 'all',
        senderId,
        senderName,
        expiresAt
      };

      console.log('NotificationService: Prepared data for all users:', notificationData);
      
      const result = await this.createNotification(notificationData);
      console.log('NotificationService: Successfully sent to all users, ID:', result);
      
      return result;
    } catch (error) {
      console.error('NotificationService: Error sending notification to all users:', error);
      throw error;
    }
  }

  // Send notification to specific user
  static async sendToUser(
    userId: string,
    title: string, 
    message: string, 
    type: 'info' | 'success' | 'warning' | 'error',
    senderId: string,
    senderName: string,
    expiresAt?: Date
  ): Promise<string> {
    try {
      const notificationData: CreateNotificationData = {
        title,
        message,
        type,
        recipientId: userId,
        senderId,
        senderName,
        expiresAt
      };

      return await this.createNotification(notificationData);
    } catch (error) {
      console.error('Error sending notification to user:', error);
      throw error;
    }
  }

}
